import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as S from './Drawer.styles';
import { CloseIcon } from '@/components/UI/Icons/Icons';
import { DrawerProps, isDrawerTitle } from '@/components/Drawer/Drawer.types';
import {
  SearchInput,
  SearchInputRef,
} from '@/components/SearchInput/SearchInput';
import { Portal } from '@/components/UI/Portal/Portal';
import NoSsr from '@/components/NoSsr/NoSsr';
import { useBodyOverflow } from '@/hooks/useBodyOverflow';
import { useAnimatedVisibility } from '@/hooks/useAnimatedVisibility';
import { useModalKeyboard } from '@/hooks/useModalKeyboard';

export const Drawer: React.FC<PropsWithChildren<DrawerProps>> = ({
  children,
  isOpen,
  close,
  closePosition = 'left',
  title,
  subtitle,
  left,
  right,
  bottom,
  hasBottomMargin = true,
  hasBottomBorder = true,
  onSearch,
  searchPlaceholder,
  isInPortal,
  className,
  dataTestId,
  isHeadFixed = true,
  capitalizeTitle,
  isHeadHidden,
  isLoading,
  background,
  isAdaptive,
}) => {
  useBodyOverflow(isOpen);
  const { isVisible, onAnimationEnd } = useAnimatedVisibility(isOpen);
  const { t } = useTranslation();
  const searchRef = useRef<SearchInputRef>(null);
  const [drawerChildrenEl, setDrawerChildrenEl] =
    useState<HTMLDivElement | null>(null);
  const { isKeyboardOpen } = useModalKeyboard(isVisible, drawerChildrenEl);

  useEffect(() => {
    searchRef.current?.clear();
  }, [isOpen]);

  function renderDrawerContent() {
    return (
      <>
        {!isHeadHidden && (
          <S.DrawerItem
            $isHead
            $isLast={!subtitle && hasBottomBorder && !onSearch}
            $hasBottomMargin={hasBottomMargin && !onSearch}
          >
            {((close && closePosition === 'left') || left) &&
              (left?.custom ? (
                <S.DrawerLeftActionContainer $isAdaptive={isAdaptive}>
                  {left.custom}
                </S.DrawerLeftActionContainer>
              ) : (
                <S.DrawerClose
                  data-testid="drawer-close-button"
                  $shape="square"
                  $appearance="transparent"
                  $isAdaptive={isAdaptive}
                  $position={closePosition}
                  onClick={left ? left.onClick : close}
                >
                  {left ? left.icon : <CloseIcon />}
                </S.DrawerClose>
              ))}

            {typeof title === 'string' && (
              <S.DrawerSimpleTitle
                $isAdaptive={isAdaptive}
                $capitalizeTitle={capitalizeTitle}
              >
                {title}
              </S.DrawerSimpleTitle>
            )}

            {React.isValidElement(title) && title}

            {isDrawerTitle(title) && title.iconSrc && title.text && (
              <S.DrawerTitle
                $isAdaptive={isAdaptive}
                $capitalizeTitle={capitalizeTitle}
              >
                <img
                  src={title.iconSrc}
                  alt=""
                  width={15}
                  height={13}
                  loading="lazy"
                />
                {title.text}
              </S.DrawerTitle>
            )}

            {((close && closePosition === 'right') || right) &&
              (right ? (
                <S.RightAction
                  appearance="primaryText"
                  onClick={right.onClick}
                  disabled={right.isDisabled}
                  $isAdaptive={isAdaptive}
                >
                  {right?.title ?? t('common.reset')}
                </S.RightAction>
              ) : (
                <S.DrawerClose
                  data-testid="drawer-close-button"
                  $shape="square"
                  $appearance="transparent"
                  $isAdaptive={isAdaptive}
                  $position={closePosition}
                  onClick={close}
                >
                  <CloseIcon />
                </S.DrawerClose>
              ))}
          </S.DrawerItem>
        )}

        {subtitle}

        {onSearch && (
          <SearchInput
            onSearch={onSearch}
            placeholder={searchPlaceholder}
            ref={searchRef}
            isLoading={isLoading}
          />
        )}
        <S.DrawerChildren ref={setDrawerChildrenEl} $isHeadFixed={isHeadFixed}>
          {children}
        </S.DrawerChildren>
      </>
    );
  }

  function renderDrawer() {
    return (
      <S.DrawerBox
        data-testid={dataTestId}
        $isOpen={isOpen}
        $isKeyboardOpen={isKeyboardOpen}
        className={className}
        $background={background}
        onAnimationEnd={onAnimationEnd}
      >
        {isHeadFixed ? (
          renderDrawerContent()
        ) : (
          <S.DrawerContentBox>{renderDrawerContent()}</S.DrawerContentBox>
        )}

        {bottom && <S.DrawerBottom>{bottom}</S.DrawerBottom>}
      </S.DrawerBox>
    );
  }

  if (!isVisible) return null;

  if (isInPortal) {
    return (
      <Portal>
        <NoSsr>{renderDrawer()}</NoSsr>
      </Portal>
    );
  }

  return renderDrawer();
};
